import React from "react";
import { useTranslation } from "react-i18next";
import { Document as D, Folder as F, Identifiable } from "@planzone/dto";
import { events } from "@planzone/events";
import { FileInput } from "@scenes/documentUpload/fileInput";
import { Preview } from "@common/preview";
import { useUploadDocument } from "@planzone/view-models";
import { Loader } from "@common/loader";
import { useMessage } from "@components/useMessage";
import { SideBar } from "../sideBar";
import { useSelectDocumentsContext } from "../context";
import { DropZone } from "./dropZone";
import { Path } from "./path";
import { DropPreview } from "./dropPreview";
import { Props } from "./types";
import { SelectionSummary } from "./selectionSummary";
import { UploadIcon } from "./uploadIcon";
import { FolderCard } from "./folderCard";
import { DocumentCard } from "./documentCard";
import styles from "./styles.scss";

function FilesSystem(_: Props) {
  const [selectedDocument, setSelectedDocument] =
    React.useState<Identifiable<D> | null>(null);
  const { t } = useTranslation();
  const {
    loading,
    project,
    currentFolderPath,
    currentFolder,
    setCurrentFolder,
    addToSelection,
  } = useSelectDocumentsContext();
  const { isRequestPending, uploadDocument } = useUploadDocument(currentFolder);

  const currentFolders = React.useMemo(() => {
    return currentFolder.children.filter(
      (child) => child.type === "folder",
    ) as Identifiable<F>[];
  }, [currentFolder]);

  const currentDocuments = React.useMemo(
    () =>
      currentFolder.children.filter(
        (child) => child.type === "document",
      ) as Identifiable<D>[],
    [currentFolder],
  );

  useMessage(({ type, data }) => {
    type === events.uploadDocumentSucceeded && addToSelection(data.document);
  });

  return (
    <div className={styles.fileSystem}>
      <div className={styles.header}>
        <FileInput
          className={styles.uploadDocumentButton}
          placeholder={t("selectDocuments.uploadDocument")}
          icon={() => <UploadIcon className={styles.icon} />}
          input={(file) =>
            uploadDocument({
              folder: currentFolder,
              namespace: project.uuid,
              type: "project",
              file,
            })
          }
        />
      </div>
      {loading || isRequestPending ? (
        <div className={styles.loader}>
          <div className={styles.loaderMain}>
            <Loader />
          </div>
        </div>
      ) : (
        <div className={styles.content}>
          <SideBar />
          <DropZone
            className={styles.main}
            drop={(file) =>
              !isRequestPending &&
              uploadDocument({
                folder: currentFolder,
                namespace: project.uuid,
                type: "project",
                file,
              })
            }
          >
            {({ isDraggingOver }) => (
              <div>
                <div>
                  {currentFolderPath.length > 0 && (
                    <Path
                      project={project}
                      path={currentFolderPath}
                      className={styles.path}
                      onNavigate={(projectId, folderId) => {
                        setCurrentFolder(folderId);
                      }}
                    />
                  )}
                  <SelectionSummary />
                </div>
                <div className={styles.documentsType}>
                  {currentFolders.length > 0 && (
                    <div>
                      <h3>{t("selectDocuments.folders")}</h3>
                      <ul>
                        {currentFolders.map((folder) => (
                          <FolderCard key={folder.uuid} folder={folder} />
                        ))}
                      </ul>
                    </div>
                  )}
                  {currentDocuments.length > 0 && (
                    <div>
                      <h3>{t("selectDocuments.documents")}</h3>
                      <ul>
                        {currentDocuments.map((document) => (
                          <DocumentCard
                            key={document.uuid}
                            document={document}
                            openPreview={setSelectedDocument}
                          />
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                {isDraggingOver && (
                  <div className={styles.dropPreview}>
                    <DropPreview />
                  </div>
                )}
              </div>
            )}
          </DropZone>
        </div>
      )}
      {selectedDocument && (
        <Preview
          document={selectedDocument}
          close={() => setSelectedDocument(null)}
        />
      )}
    </div>
  );
}

export { FilesSystem };
